import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook to manage PWA installation for the kiosk app.
 *
 * Captures the `beforeinstallprompt` event and exposes:
 * - canInstall: whether the browser supports PWA install
 * - isInstalled: whether the app is already running as a standalone PWA
 * - promptInstall: function to trigger the native install prompt
 */
export function useKioskInstall() {
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const deferredPromptRef = useRef(null);

  // Detect if already running as standalone PWA
  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    setIsInstalled(isStandalone);

    // Listen for display mode changes
    const mq = window.matchMedia('(display-mode: standalone)');
    const handler = (e) => setIsInstalled(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Capture the beforeinstallprompt event
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful install
    const installHandler = () => {
      setIsInstalled(true);
      setCanInstall(false);
      deferredPromptRef.current = null;
    };
    window.addEventListener('appinstalled', installHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installHandler);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPromptRef.current) return false;

    deferredPromptRef.current.prompt();
    const { outcome } = await deferredPromptRef.current.userChoice;
    deferredPromptRef.current = null;

    if (outcome === 'accepted') {
      setCanInstall(false);
      return true;
    }

    return false;
  }, []);

  return {
    canInstall,
    isInstalled,
    promptInstall,
  };
}
